/**
 * PermissionPrompt — allow/deny UI for tool permission requests
 *
 * @sync OpenCode → packages/opencode/src/cli/cmd/tui/routes/session/permission.tsx
 *
 * Shows tool-specific input previews (file paths, commands, URLs)
 * and action buttons (Allow once, Always allow, Deny).
 */
import { useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { Shield, Check, X, ToggleLeft, FileEdit, Terminal, Globe, Search, FileText } from 'lucide-react'
import type { PermissionRequest } from './hooks.js'

const DAY_MS = 24 * 60 * 60 * 1000
/** #648: whole days since a prompt was first asked — a prompt now waits as long as the person takes; under a day it is
 *  shown as it always was. Null when the request carries no time (one from before it was stamped). */
export function promptAgeDays(askedAt: number | undefined, now: number = Date.now()): number | null {
  if (typeof askedAt !== 'number' || !Number.isFinite(askedAt)) return null
  const days = Math.floor((now - askedAt) / DAY_MS)
  return days >= 1 ? days : null
}

interface Props {
  request: PermissionRequest
  onReply: (action: 'allow' | 'deny', always?: boolean, feedback?: string) => void
}

/** Get a human-readable title and icon for the permission type */
function getPermissionDisplay(request: PermissionRequest): { title: string; icon: React.ReactNode } {
  const meta = request.metadata
  const perm = request.permission

  switch (perm) {
    case 'bash':
      return {
        title: 'Run command',
        icon: <Terminal size={14} className="text-yellow-400" />,
      }
    case 'edit_file':
      return {
        title: `Edit ${(meta.path as string) || request.patterns[0] || 'file'}`,
        icon: <FileEdit size={14} className="text-yellow-400" />,
      }
    case 'write_file':
      return {
        title: `${meta.action === 'restore' ? 'Restore' : 'Write'} ${(meta.path as string) || request.patterns[0] || 'file'}`,
        icon: <FileText size={14} className="text-yellow-400" />,
      }
    case 'move_file':
      return {
        title: `Move ${(meta.path as string) || 'item'} → ${(meta.destination as string) || 'drive root'}${meta.newName ? ` as ${meta.newName as string}` : ''}`,
        icon: <FileEdit size={14} className="text-yellow-400" />,
      }
    case 'webfetch':
      return {
        title: 'Fetch URL',
        icon: <Globe size={14} className="text-yellow-400" />,
      }
    case 'task':
      return {
        title: 'Launch agent task',
        icon: <Search size={14} className="text-yellow-400" />,
      }
    case 'office_tool':
    case 'libre_office_tool':
      return {
        title: (meta.description as string) || 'Edit document',
        icon: <FileEdit size={14} className="text-yellow-400" />,
      }
    default:
      return {
        title: request.description || perm,
        icon: <Shield size={14} className="text-yellow-400" />,
      }
  }
}

/** Render tool-specific input details */
function ToolInputPreview({ request }: { request: PermissionRequest }) {
  const meta = request.metadata
  const perm = request.permission

  switch (perm) {
    case 'bash': {
      const command = (meta.command as string) || request.patterns[0]
      const reason = meta.reason as string | undefined
      if (!command) return null
      return (
        <div className="mt-1.5 rounded bg-black/20 px-2 py-1.5 font-mono text-[11px]">
          {reason && (
            <p className="text-yellow-400/70 text-[10px] mb-1">{reason}</p>
          )}
          <p className="themed-text break-all">$ {command}</p>
        </div>
      )
    }

    case 'edit_file': {
      const path = (meta.path as string) || request.patterns[0]
      const patches = meta.patches as Array<{ oldText: string; newText: string }> | undefined
      const occurrences = meta.occurrences as number | undefined
      return (
        <div className="mt-1.5 space-y-1">
          {path && (
            <p className="text-[10px] font-mono themed-text-muted truncate">{path}</p>
          )}
          {occurrences != null && occurrences > 1 && (
            <p className="text-[10px] themed-text-muted">{occurrences} replacements</p>
          )}
          {patches && patches.length > 0 && (
            <div className="rounded bg-black/20 px-2 py-1 font-mono text-[10px] max-h-24 overflow-y-auto">
              {patches.slice(0, 3).map((patch, i) => (
                <div key={i} className="mb-1">
                  <p className="text-red-400/70 truncate">- {patch.oldText.slice(0, 80)}</p>
                  <p className="text-green-400/70 truncate">+ {patch.newText.slice(0, 80)}</p>
                </div>
              ))}
              {patches.length > 3 && (
                <p className="themed-text-muted">...and {patches.length - 3} more</p>
              )}
            </div>
          )}
        </div>
      )
    }

    case 'write_file': {
      const path = (meta.path as string) || request.patterns[0]
      const size = meta.size as number | undefined
      const description = meta.description as string | undefined
      const versionId = meta.versionId as string | undefined
      return (
        <div className="mt-1.5">
          {path && (
            <p className="text-[10px] font-mono themed-text-muted truncate">{path}</p>
          )}
          {versionId && (
            <p className="text-[10px] themed-text-muted mt-0.5">Version: {versionId}</p>
          )}
          {description && (
            <p className="text-[10px] themed-text-muted mt-0.5">{description}</p>
          )}
          {size != null && (
            <p className="text-[10px] themed-text-muted mt-0.5">{size.toLocaleString()} chars</p>
          )}
        </div>
      )
    }

    case 'webfetch': {
      const url = meta.url as string | undefined
      return url ? (
        <div className="mt-1.5">
          <p className="text-[10px] font-mono themed-text-muted truncate">{url}</p>
        </div>
      ) : null
    }

    case 'task': {
      return request.patterns.length > 0 ? (
        <div className="mt-1.5">
          <p className="text-[10px] themed-text-muted">{request.patterns[0]}</p>
        </div>
      ) : null
    }

    case 'libre_office_tool': {
      const script = meta.script as string | undefined
      return script ? (
        <div className="mt-1.5 rounded bg-black/20 px-2 py-1.5 font-mono text-[10px] max-h-24 overflow-y-auto">
          <p className="themed-text-muted whitespace-pre-wrap break-all">{script}</p>
        </div>
      ) : null
    }

    case 'office_tool': {
      // officecli edits carry { description, file } (the commands are not
      // echoed here) — show the target file.
      const file = meta.file as string | undefined
      return file ? (
        <div className="mt-1.5 rounded bg-black/20 px-2 py-1.5 font-mono text-[10px]">
          <p className="themed-text-muted truncate">{file}</p>
        </div>
      ) : null
    }

    default:
      return request.patterns.length > 0 ? (
        <div className="mt-1.5">
          <p className="text-[10px] font-mono themed-text-muted truncate">
            {request.patterns.join(', ')}
          </p>
        </div>
      ) : null
  }
}

export function PermissionPrompt({ request, onReply }: Props) {
  const { t } = useTranslation('agent')
  const { title, icon } = getPermissionDisplay(request)
  const ageDays = promptAgeDays(request.askedAt)
  // #648: re-asked on resume because what this call replaces changed since the person answered (resume-safety.ts)
  const changed = request.metadata?.resumeReasked === 'changed'
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState('')

  const handleDeny = () => {
    onReply('deny', false, feedback || undefined)
  }

  return (
    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 my-2">
      <div className="flex items-start gap-2">
        <div className="shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium themed-text">{title}</p>
          {ageDays !== null && (
            <p className="text-[10px] themed-text-muted mt-0.5" data-testid="prompt-age">{t('permission.askedAgo', { count: ageDays })}</p>
          )}
          {changed && (
            <p className="text-[11px] text-yellow-400 mt-1" data-testid="prompt-changed">{t('permission.changedSinceAnswer')}</p>
          )}
          <ToolInputPreview request={request} />

          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={() => onReply('allow')}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-green-600 hover:bg-green-500 text-white transition-colors"
            >
              <Check size={10} />
              Allow
            </button>
            {/* No "Always" where none is offered (every dangerous bash command — always-grant.ts): it is asked each time. */}
            {request.alwaysOffered !== false && (
              <button
                onClick={() => onReply('allow', true)}
                className="flex items-center gap-1 px-2.5 py-1 rounded text-xs themed-bg-tertiary themed-text hover:opacity-80 transition-opacity"
              >
                <ToggleLeft size={10} />
                Always
              </button>
            )}
            <button
              onClick={() => showFeedback ? handleDeny() : setShowFeedback(true)}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-red-600/20 hover:bg-red-600/30 text-red-400 transition-colors"
            >
              <X size={10} />
              Deny
            </button>
          </div>

          {/* Deny feedback input */}
          {showFeedback && (
            <div className="flex items-center gap-1.5 mt-2">
              <input
                type="text"
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleDeny() }}
                placeholder="Tell the AI why, or what to do instead (optional)"
                className="flex-1 text-xs px-2 py-1 rounded bg-black/10 themed-text themed-border border outline-none focus:border-yellow-500/50"
                autoFocus
              />
              <button
                onClick={handleDeny}
                className="text-xs px-2 py-1 rounded bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors shrink-0"
              >
                Deny
              </button>
              <button
                onClick={() => { setShowFeedback(false); onReply('deny') }}
                className="text-xs px-2 py-1 rounded themed-bg-tertiary themed-text-muted hover:themed-text transition-colors shrink-0"
              >
                Skip
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
